import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { DisciplineFilePack } from './interfaces'

import { useListsStore } from '@shared/model'
import { useAdminRoutes } from '@shared/lib/routes'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { DisciplineFilesModal } from './enums'
import { useDisciplineFilesStore } from './files/store'
import { getPackCategoryLabel, getPackFilesText } from './discipline-files'
import { formatTaskDate } from './tutor-tasks/tutor-tasks'

interface DisciplineFilePackRow {
  pack: DisciplineFilePack
  sentAt: string
  tutorName: string | null
  tutorUrl: string | null
  categoryLabel: string
  filesText: string | null
}

interface TableDisciplineFilesReturn {
  rows: ComputedRef<DisciplineFilePackRow[]>
  isLoading: ComputedRef<boolean>
  hasPacks: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  openPack: (pack: DisciplineFilePack) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableDisciplineFiles = (): TableDisciplineFilesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useDisciplineFilesStore()
  const listsStore = useListsStore()

  const isLoading = computed<boolean>(() => store.isLoading)
  const hasPacks = computed<boolean>(() => store.hasPacks)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const rows = computed<DisciplineFilePackRow[]>(() => {
    const fileLists = listsStore.getLists?.file ?? null

    return store.getPacks.map(pack => ({
      pack,
      sentAt: formatTaskDate(pack.sent_at),
      tutorName: pack.tutor?.full_name ?? null,
      tutorUrl: pack.tutor ? useAdminRoutes('admin.tutor.show', { tutor_id: pack.tutor.id }) : null,
      categoryLabel: getPackCategoryLabel(pack.owner_type, pack.category, fileLists),
      filesText: getPackFilesText(pack.files_count),
    }))
  })

  const openPack = (pack: DisciplineFilePack): void => {
    store.openModal(DisciplineFilesModal.Pack, pack.id)
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    rows,
    isLoading,
    hasPacks,
    meta,
    perPageOptions,
    openPack,
    setChangePage,
    setChangePerPage,
  }
}
